import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import api from '../../utils/api';
import { getImageUrl } from '../../utils/imageUrl';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

export default function FeaturedPage() {
  const navigate = useNavigate();
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch featured products
  useEffect(() => {
    const fetchFeaturedProducts = async () => {
      try {
        const response = await api.get('/home/featured');
        if (response.data.success) {
          setAllProducts(response.data.data);
        }
      } catch (error) {
        console.error('Failed to fetch featured products:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedProducts();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Responsive breakpoints
  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth < 1024;

  // Styles
  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: '#FFFFFF',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      padding: isMobile ? '40px 20px' : isTablet ? '60px 30px' : '80px 60px',
      fontFamily: 'Arial, sans-serif',
      boxSizing: 'border-box',
    },
    title: {
      fontSize: isMobile ? '36px' : isTablet ? '48px' : '60px',
      fontWeight: '300',
      color: '#1A1A1A',
      marginBottom: isMobile ? '40px' : '60px',
      letterSpacing: '3px',
      textAlign: 'center',
    },
    carouselContainer: {
      width: '100%',
      maxWidth: '1400px',
      position: 'relative',
      boxSizing: 'border-box',
    },
    productCard: {
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: '#FAFAFA',
      borderRadius: '8px',
      padding: isMobile ? '12px' : '14px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
      boxSizing: 'border-box',
      overflow: 'hidden',
      cursor: 'pointer',
      height: '100%',
    },
    productImage: {
      width: '100%',
      height: isMobile ? '180px' : isTablet ? '200px' : '240px',
      objectFit: 'cover',
      borderRadius: '6px',
      marginBottom: '12px',
      display: 'block',
    },
    productName: {
      fontSize: isMobile ? '13px' : '14px',
      fontWeight: '600',
      color: '#1A1A1A',
      margin: '6px 0',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    productPrice: {
      fontSize: isMobile ? '15px' : '17px',
      fontWeight: '700',
      color: '#1A1A1A',
      marginTop: '4px',
    },
    loading: {
      textAlign: 'center',
      padding: '60px 20px',
      fontSize: '1.2rem',
      color: '#666',
    },
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading featured products...</div>
      </div>
    );
  }

  if (allProducts.length === 0) {
    return (
      <div style={styles.container}>
        <h1 style={styles.title}>FEATURED</h1>
        <div style={styles.loading}>No featured products available</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>FEATURED</h1>

      <div style={styles.carouselContainer}>
        <Swiper
          modules={[Navigation, Pagination, Autoplay]}
          spaceBetween={isMobile ? 12 : isTablet ? 18 : 22}
          slidesPerView={isMobile ? 2 : isTablet ? 3 : 4}
          navigation={{
            nextEl: '.swiper-button-next-custom',
            prevEl: '.swiper-button-prev-custom',
          }}
          pagination={{ 
            clickable: true,
            dynamicBullets: true,
          }}
          autoplay={{
            delay: 3000,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          }}
          loop={true}
          loopAdditionalSlides={2}
          speed={800}
          grabCursor={true}
          className="featured-swiper"
        >
          {allProducts.map((product) => (
            <SwiperSlide key={product.id}>
              <ProductCard 
                product={product} 
                styles={styles} 
                navigate={navigate}
              />
            </SwiperSlide>
          ))}
        </Swiper>

        {/* Custom Navigation Buttons */}
        <div className="swiper-button-prev-custom">
          ←
        </div>
        <div className="swiper-button-next-custom">
          →
        </div>
      </div>

      <style>{`
        .featured-swiper {
          padding: 30px 0 60px 0;
        }

        .featured-swiper .swiper-pagination {
          bottom: 10px;
        }

        .featured-swiper .swiper-pagination-bullet {
          width: ${isMobile ? '8px' : '10px'};
          height: ${isMobile ? '8px' : '10px'};
          background: #D0D0D0;
          opacity: 1;
        }

        .featured-swiper .swiper-pagination-bullet-active {
          background: #1A1A1A;
        }

        .swiper-button-prev-custom,
        .swiper-button-next-custom {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: ${isMobile ? '44px' : '54px'};
          height: ${isMobile ? '44px' : '54px'};
          border-radius: 50%;
          background: transparent;
          border: 2px solid #1A1A1A;
          color: #1A1A1A;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          z-index: 10;
          font-size: ${isMobile ? '18px' : '22px'};
          transition: all 0.2s ease;
          user-select: none;
        }

        .swiper-button-prev-custom:hover,
        .swiper-button-next-custom:hover {
          background: #1A1A1A;
          color: #FFFFFF;
        }

        .swiper-button-prev-custom {
          left: -20px;
        }

        .swiper-button-next-custom {
          right: -20px;
        }

        @media (max-width: 768px) {
          .swiper-button-prev-custom {
            left: 0;
          }

          .swiper-button-next-custom {
            right: 0;
          }
        }
      `}</style>
    </div>
  );
}

// Separate ProductCard component for hover effects
function ProductCard({ product, styles, navigate }) {
  const [isHovered, setIsHovered] = useState(false);

  const handleClick = () => {
    if (product.slug) {
      navigate(`/product/${product.slug}`);
    }
  };

  return (
    <div
      style={{
        ...styles.productCard,
        transform: isHovered ? 'translateY(-6px)' : 'translateY(0)',
        boxShadow: isHovered ? '0 6px 18px rgba(0,0,0,0.12)' : '0 2px 8px rgba(0,0,0,0.08)',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleClick}
    >
      <img 
        src={getImageUrl(product.image_url)}
        alt={product.name} 
        style={styles.productImage}
        onError={(e) => {
          e.target.src = 'https://via.placeholder.com/400x500?text=No+Image';
        }}
      />
      <h3 style={styles.productName}>{product.name}</h3>
      <p style={styles.productPrice}>₾{parseFloat(product.price).toFixed(2)}</p>
    </div>
  );
}